import { ApiLanguage } from '@ianesthesie-kit/config';
import { parseDateOrNull } from '@ianesthesie-kit/core';
import { Audited, BaseEntity, Hospital, User } from '@ianesthesie-kit/services';
import { differenceInCalendarMonths } from 'date-fns';
import type { ConventionStatus } from '@/modules/billing/billing.model';
import {
  CustomGraph,
  GraphType,
  MedicalHoContentType,
  MedicalHoViewMode,
  TableScrollMode
} from '@/modules/ui';
import { PatientDetailsCardId } from '@/pages-client/patientDetails/components/patientDetailsCardId';
import type { BillingDashboardUserFilters } from '@/modules/billing/billingLine.model';
import {
  PeropFilters,
  PostopFilters,
  PreopDayViewFilters,
  PreopSurgeryPlanningFilters,
  PreopView
} from '@/modules/surgicalEvent';
import { DataType } from '@/modules/terra';
import {
  DashboardGroupingStatus,
  PatientSearchType,
  TriageGroup
} from '@/modules/appContext';
import { NlpFinding } from '@/modules/nlp';

declare module '@ianesthesie-kit/services' {
  interface User {
    conventionStatus?: ConventionStatus;
  }
}

export type UserAppSettings = {
  tableScrollMode?: TableScrollMode;
  tableDateCellView?: RelAbs;
  tableCollapsedState?: {
    [group in TriageGroup]?: { [status in DashboardGroupingStatus]?: boolean };
  };
  pdvExpandedCards?: { [k in PatientDetailsCardId]?: boolean };
  pdvEpicDocumentsFilter?: EpicDocumentsFilter;
  pdvEpicDocumentsPeriod?: MhoPeriod;
  nlpEntityConfidenceThreshold?: number;
  nlpConceptConfidenceThreshold?: number;
  nlpEntityCategory?: NlpFinding['category'] | 'ALL';
  nlpExcludeNegations?: boolean;
  nlpMhoSuggestionPeriod?: MhoPeriod;
  nlpMedicationPeriod?: MhoPeriod;
  medicalHoViewMode?: MedicalHoViewMode;
  medicalHoContentType?: MedicalHoContentType;
  preopView?: PreopView;
  patientSearch?: PatientSearchType;
  preopSurgeryPlanningFilters?: PreopSurgeryPlanningFilters;
  preopDayViewFilters?: PreopDayViewFilters;
  peropFilters?: PeropFilters;
  postopFilters?: PostopFilters;
  billingDashboardFilters?: BillingDashboardUserFilters;
  postopGraph?: {
    type?: GraphType;
    customGraphName?: string;
  };
  customGraphs?: { [k in DataType]?: CustomGraph[] };
  newRoutesViewed?: string[];
  patientsAppIntegrationEnabled?: boolean;
};

export type RelAbs = 'rel' | 'abs';

export type EpicDocumentsFilter = 'all' | 'redFlag';
export type MhoPeriod = '6M' | '12M' | '24M' | '5Y' | 'MAX';

export const NLP_CONFIG_DEFAULT_THRESHOLD = 0.75;
export const NLP_CONFIG_DEFAULT_MHO_SUGGESTION_PERIOD: MhoPeriod = '24M';
export const NLP_CONFIG_DEFAULT_MEDICATION_PERIOD: MhoPeriod = '12M';
export const PDV_CONFIG_DEFAULT_EPIC_DOCUMENTS_PERIOD: MhoPeriod = 'MAX';

const NOW = Date.now();
export function isWithinMhoPeriod(
  date: string | Date | null | undefined,
  period: MhoPeriod
) {
  const d = parseDateOrNull(date);
  if (!d || !period) {
    return false;
  }
  if (period === 'MAX') {
    return true;
  }
  const monthsLimit = period === '5Y' ? 60 : +period.slice(0, -1);
  return differenceInCalendarMonths(NOW, d) <= monthsLimit;
}

export interface UserConfig extends BaseEntity {
  language: ApiLanguage;
  profilePicId: string;
  settings?: UserAppSettings;
}

export interface DoctorHospital extends Audited {
  adeli?: string;
  active: boolean;
  hospitalAdmin: boolean;
  hospital: Hospital & {
    logoPath?: string;
  };
}

export type MedicalCredential =
  | 'MD'
  | 'DO'
  | 'MBBS'
  | 'RN'
  | 'NP'
  | 'DNP'
  | 'APRN'
  | 'CRNA'
  | 'MA'
  | 'SC'
  | string;

export const nurseMedicalCredentials = [
  'RN',
  'NP',
  'DNP',
  'APRN',
  'CRNA'
] as const;

/** Credentials of a physician, the only ones addressed as "Dr". */
export const physicianMedicalCredentials = ['MD', 'DO', 'MBBS'] as const;

export const isPhysician = (user: {
  medicalCredentials?: MedicalCredential;
}): boolean =>
  !!user.medicalCredentials &&
  (physicianMedicalCredentials as readonly string[]).includes(
    user.medicalCredentials
  );

type MinimalDoctorProps =
  | 'id'
  | 'firstName'
  | 'middleName'
  | 'lastName'
  | 'doctorIdentifier'
  | 'medicalCredentials'
  | 'email'
  | 'phoneNumber'
  | 'gender'
  | 'signature'
  | 'conventionStatus';

export type MinimalDoctorDto = Pick<User, MinimalDoctorProps>;

/**
 * Whether a hospital roster entry is a secretary rather than a billing/clinical doctor.
 * Mirrors doctor-bff's own `isSecretary()` discriminator (BeanConfig, `MedicalCredentials.SC`) —
 * secretaries are stored as doctors with `medicalCredentials === 'SC'`.
 */
export const isSecretary = (
  user: Pick<MinimalDoctorDto, 'medicalCredentials'>
): boolean => user.medicalCredentials === 'SC';

export type SearchByIdOrPersonalInfoParams = {
  firstName?: string;
  lastName?: string;
  birthDate?: string;
  identifier?: string;
};
